import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { productsAPI } from '../utils/api';
import { Upload, FileText, AlertCircle, CheckCircle, Edit2, Trash2, DollarSign, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export const PDFImport = () => {
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [jobId, setJobId] = useState(null);
  const [jobStatus, setJobStatus] = useState(null);
  const [extractedProducts, setExtractedProducts] = useState(null);
  const [editingIndex, setEditingIndex] = useState(null);
  const [editedProduct, setEditedProduct] = useState(null);
  const [importing, setImporting] = useState(false);
  const [showDuplicates, setShowDuplicates] = useState(true);
  const pollingInterval = useRef(null);

  // Poll job status
  useEffect(() => {
    if (jobId && jobStatus?.status !== 'completed' && jobStatus?.status !== 'failed') {
      pollingInterval.current = setInterval(async () => {
        try {
          const response = await productsAPI.getPDFImportStatus(jobId);
          setJobStatus(response.data);
          
          if (response.data.status === 'completed') {
            clearInterval(pollingInterval.current);
            setExtractedProducts(response.data.result.products);
            setUploading(false);
            
            const totalProducts = response.data.result.products_found;
            const duplicates = response.data.result.duplicates_found || 0;
            
            if (duplicates > 0) {
              toast.warning(`${totalProducts} products extracted. ${duplicates} duplicate(s) found - review before importing.`);
            } else {
              toast.success(`${totalProducts} products extracted successfully!`);
            }
          } else if (response.data.status === 'failed') {
            clearInterval(pollingInterval.current);
            setUploading(false);
            toast.error(response.data.error || 'PDF processing failed');
          }
        } catch (error) {
          console.error('Status polling error:', error);
        }
      }, 3000); // Poll every 3 seconds
      
      return () => clearInterval(pollingInterval.current);
    }
  }, [jobId, jobStatus?.status]);

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.type === 'application/pdf') {
      setFile(selectedFile);
      setExtractedProducts(null);
      setJobId(null);
      setJobStatus(null);
    } else {
      toast.error('Please select a valid PDF file');
    }
  };

  const handleUploadAndExtract = async () => {
    if (!file) {
      toast.error('Please select a PDF file first');
      return;
    }

    setUploading(true);
    try {
      const response = await productsAPI.importPDF(file);
      
      if (response.data.success && response.data.job_id) {
        setJobId(response.data.job_id);
        toast.success('PDF uploaded! Processing started...');
      } else {
        toast.error('Failed to start PDF processing');
        setUploading(false);
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload PDF');
      setUploading(false);
    }
  };

  const handleEditProduct = (index) => {
    setEditingIndex(index);
    setEditedProduct({ ...extractedProducts[index] });
  };

  const handleSaveEdit = () => {
    const updated = [...extractedProducts];
    updated[editingIndex] = editedProduct;
    setExtractedProducts(updated);
    setEditingIndex(null);
    setEditedProduct(null);
    toast.success('Product updated');
  };

  const handleCancelEdit = () => {
    setEditingIndex(null);
    setEditedProduct(null);
  };

  const handleDeleteProduct = (index) => {
    const updated = extractedProducts.filter((_, i) => i !== index);
    setExtractedProducts(updated);
    toast.success('Product removed');
  };

  const handleConfirmImport = async () => {
    setImporting(true);
    try {
      // Filter out duplicates if showDuplicates is false
      const productsToImport = showDuplicates 
        ? extractedProducts 
        : extractedProducts.filter(p => !p.is_duplicate);
      
      if (productsToImport.length === 0) {
        toast.error('No products to import. All are duplicates.');
        setImporting(false);
        return;
      }
      
      const response = await productsAPI.confirmPDFImport(productsToImport);
      
      if (response.data.success) {
        const skipped = extractedProducts.length - productsToImport.length;
        if (skipped > 0) {
          toast.success(`${response.data.imported_count} products imported successfully! ${skipped} duplicate(s) skipped.`);
        } else {
          toast.success(`${response.data.imported_count} products imported successfully!`);
        }
        navigate('/catalogue');
      } else {
        toast.error('Failed to import products');
      }
    } catch (error) {
      console.error('Import error:', error);
      toast.error(error.response?.data?.detail || 'Failed to import products');
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="font-barlow text-4xl font-bold text-slate-900 mb-2">
            Import Products from PDF
          </h1>
          <p className="text-slate-600">Upload a PDF catalog and let AI extract product information</p>
        </div>

        {!extractedProducts && !uploading ? (
          <div className="bg-white rounded-xl border border-slate-200 p-8 shadow-sm">
            <div className="max-w-md mx-auto">
              <div className="flex flex-col items-center">
                <div className="w-16 h-16 bg-consortium-green-light rounded-full flex items-center justify-center mb-4">
                  <FileText className="w-8 h-8 text-consortium-green" />
                </div>
                
                <h2 className="text-2xl font-bold text-slate-900 mb-2">Upload PDF Catalog</h2>
                <p className="text-slate-600 text-center mb-6">
                  Select a PDF file containing product information. AI will extract product details, images, and prices.
                </p>

                <label className="w-full cursor-pointer">
                  <div className="border-2 border-dashed border-slate-300 rounded-lg p-8 hover:border-consortium-green transition-colors text-center">
                    <Upload className="w-12 h-12 text-slate-400 mx-auto mb-3" />
                    <p className="text-slate-700 font-medium mb-1">
                      {file ? file.name : 'Click to select PDF file'}
                    </p>
                    <p className="text-sm text-slate-500">or drag and drop</p>
                  </div>
                  <input
                    type="file"
                    accept=".pdf"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                </label>

                {file && (
                  <button
                    onClick={handleUploadAndExtract}
                    disabled={uploading}
                    className="mt-6 w-full bg-consortium-green text-white rounded-lg px-6 py-3 font-medium hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Extract Products
                  </button>
                )}
              </div>
            </div>
          </div>
        ) : uploading && jobStatus ? (
          // Progress UI
          <div className="bg-white rounded-xl border border-slate-200 p-8 shadow-sm">
            <div className="max-w-2xl mx-auto">
              <div className="flex flex-col items-center">
                <Loader2 className="w-16 h-16 text-consortium-green animate-spin mb-4" />
                
                <h2 className="text-2xl font-bold text-slate-900 mb-2">
                  Processing PDF Catalog
                </h2>
                <p className="text-slate-600 text-center mb-6">
                  AI is extracting products from your catalog. This may take several minutes for large files.
                </p>

                {/* Progress Bar */}
                <div className="w-full bg-slate-200 rounded-full h-3 mb-4">
                  <div 
                    className="bg-consortium-green h-3 rounded-full transition-all duration-500"
                    style={{ 
                      width: `${jobStatus.progress?.total_pages > 0 ? (jobStatus.progress.current_page / jobStatus.progress.total_pages) * 100 : 0}%` 
                    }}
                  ></div>
                </div>

                {/* Progress Stats */}
                <div className="w-full grid grid-cols-2 gap-4 mt-6">
                  <div className="bg-slate-50 rounded-lg p-4">
                    <div className="text-sm text-slate-600 mb-1">Pages Processed</div>
                    <div className="text-2xl font-bold text-slate-900">
                      {jobStatus.progress?.current_page || 0} / {jobStatus.progress?.total_pages || 0}
                    </div>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-4">
                    <div className="text-sm text-slate-600 mb-1">Products Found</div>
                    <div className="text-2xl font-bold text-consortium-green">
                      {jobStatus.progress?.products_found || 0}
                    </div>
                  </div>
                </div>

                {/* Chunk Progress */}
                {jobStatus.progress?.total_chunks > 0 && (
                  <div className="mt-4 text-sm text-slate-600">
                    Processing chunk {jobStatus.progress.current_chunk} of {jobStatus.progress.total_chunks}
                  </div>
                )}

                <p className="mt-6 text-xs text-slate-500 text-center">
                  You can close this tab and come back later. Processing will continue in the background.
                </p>
              </div>
            </div>
          </div>
        ) : !extractedProducts && uploading ? (
          // Initial upload state (before status is available)
          <div className="bg-white rounded-xl border border-slate-200 p-8 shadow-sm">
            <div className="max-w-md mx-auto text-center">
              <Loader2 className="w-16 h-16 text-consortium-green animate-spin mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-slate-900 mb-2">Uploading PDF...</h2>
              <p className="text-slate-600">Please wait while we upload your file</p>
            </div>
          </div>
        ) : extractedProducts ? (
          <div className="space-y-6">
            <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-6 h-6 text-green-600" />
                  <div>
                    <h2 className="text-xl font-bold text-slate-900">
                      {extractedProducts.length} Products Extracted
                    </h2>
                    <p className="text-slate-600">Review and edit before importing</p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      setExtractedProducts(null);
                      setFile(null);
                    }}
                    className="px-4 py-2 border border-slate-300 rounded-lg text-slate-700 hover:bg-slate-50 transition-colors"
                  >
                    Start Over
                  </button>
                  <button
                    onClick={handleConfirmImport}
                    disabled={importing || extractedProducts.length === 0}
                    className="px-6 py-2 bg-consortium-green text-white rounded-lg font-medium hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {importing ? (
                      <span className="flex items-center gap-2">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                        Importing...
                      </span>
                    ) : (
                      `Import ${showDuplicates ? extractedProducts.length : extractedProducts.filter(p => !p.is_duplicate).length} Products`
                    )}
                  </button>
                </div>
              </div>
              
              {/* Duplicate Filter */}
              {extractedProducts.some(p => p.is_duplicate) && (
                <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <AlertCircle className="w-5 h-5 text-yellow-600" />
                      <span className="text-yellow-900 font-medium">
                        {extractedProducts.filter(p => p.is_duplicate).length} duplicate product(s) found in database
                      </span>
                    </div>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={!showDuplicates}
                        onChange={(e) => setShowDuplicates(!e.target.checked)}
                        className="w-4 h-4 text-consortium-green border-gray-300 rounded focus:ring-consortium-green"
                      />
                      <span className="text-sm text-yellow-900">Hide duplicates</span>
                    </label>
                  </div>
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 gap-6">
              {extractedProducts.filter(p => showDuplicates || !p.is_duplicate).map((product, index) => (
                <div key={index} className={`bg-white rounded-xl border shadow-sm overflow-hidden ${product.is_duplicate ? 'border-yellow-300 bg-yellow-50/30' : 'border-slate-200'}`}>
                  {product.is_duplicate && (
                    <div className="bg-yellow-100 border-b border-yellow-300 px-4 py-2 flex items-center gap-2">
                      <AlertCircle className="w-4 h-4 text-yellow-700" />
                      <span className="text-sm font-medium text-yellow-900">
                        Duplicate detected (matches existing product by {product.duplicate_reason?.join(', ')})
                      </span>
                    </div>
                  )}
                  {editingIndex === index ? (
                    <div className="p-6">
                      <h3 className="text-lg font-bold text-slate-900 mb-4">Edit Product</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1">Product Code</label>
                          <input
                            type="text"
                            value={editedProduct.product_code || ''}
                            onChange={(e) => setEditedProduct({ ...editedProduct, product_code: e.target.value })}
                            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-consortium-green"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1">Product Name</label>
                          <input
                            type="text"
                            value={editedProduct.product_name || ''}
                            onChange={(e) => setEditedProduct({ ...editedProduct, product_name: e.target.value })}
                            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-consortium-green"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1">Category</label>
                          <input
                            type="text"
                            value={editedProduct.category || ''}
                            onChange={(e) => setEditedProduct({ ...editedProduct, category: e.target.value })}
                            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-consortium-green"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1">Price (Rs.)</label>
                          <input
                            type="number"
                            value={editedProduct.price || ''}
                            onChange={(e) => setEditedProduct({ ...editedProduct, price: e.target.value })}
                            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-consortium-green"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1">Cost Price (Rs.)</label>
                          <input
                            type="number"
                            value={editedProduct.cost_price || editedProduct.price || ''}
                            onChange={(e) => setEditedProduct({ ...editedProduct, cost_price: e.target.value })}
                            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-consortium-green"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1">MOQ</label>
                          <input
                            type="number"
                            value={editedProduct.moq || ''}
                            onChange={(e) => setEditedProduct({ ...editedProduct, moq: e.target.value })}
                            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-consortium-green"
                          />
                        </div>
                        <div className="md:col-span-2">
                          <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
                          <textarea
                            value={editedProduct.description || ''}
                            onChange={(e) => setEditedProduct({ ...editedProduct, description: e.target.value })}
                            rows={4}
                            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-consortium-green"
                          />
                        </div>
                      </div>
                      <div className="flex gap-3 mt-4">
                        <button
                          onClick={handleSaveEdit}
                          className="px-4 py-2 bg-consortium-green text-white rounded-lg hover:bg-green-700 transition-colors"
                        >
                          Save Changes
                        </button>
                        <button
                          onClick={handleCancelEdit}
                          className="px-4 py-2 border border-slate-300 rounded-lg text-slate-700 hover:bg-slate-50 transition-colors"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-col md:flex-row">
                      {product.page_image && (
                        <div className="md:w-1/3 bg-slate-100">
                          <img
                            src={product.page_image}
                            alt={product.product_name}
                            className="w-full h-full object-contain p-4"
                          />
                        </div>
                      )}
                      <div className="flex-1 p-6">
                        <div className="flex items-start justify-between mb-4">
                          <div>
                            <div className="flex items-center gap-2 mb-2">
                              <span className="px-3 py-1 bg-consortium-green-light text-consortium-green text-sm font-medium rounded-full">
                                {product.product_code || 'No Code'}
                              </span>
                              <span className="px-3 py-1 bg-slate-100 text-slate-700 text-sm rounded-full">
                                {product.category || 'Uncategorized'}
                              </span>
                            </div>
                            <h3 className="text-xl font-bold text-slate-900 mb-2">
                              {product.product_name || 'Untitled Product'}
                            </h3>
                            <div className="flex items-center gap-2 mb-3">
                              <DollarSign className="w-5 h-5 text-green-600" />
                              <span className="text-2xl font-bold text-green-600">
                                Rs. {product.price || 0}
                              </span>
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleEditProduct(index)}
                              className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                              title="Edit"
                            >
                              <Edit2 className="w-5 h-5" />
                            </button>
                            <button
                              onClick={() => handleDeleteProduct(index)}
                              className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              title="Remove"
                            >
                              <Trash2 className="w-5 h-5" />
                            </button>
                          </div>
                        </div>
                        
                        <div className="space-y-3">
                          <div>
                            <p className="text-slate-700 text-sm leading-relaxed">
                              {product.description || 'No description available'}
                            </p>
                          </div>
                          
                          {product.features && product.features.length > 0 && (
                            <div>
                              <h4 className="text-sm font-semibold text-slate-900 mb-1">Key Features:</h4>
                              <ul className="text-sm text-slate-600 space-y-1">
                                {product.features.slice(0, 5).map((feature, i) => (
                                  <li key={i} className="flex items-start gap-2">
                                    <span className="text-consortium-green mt-1">•</span>
                                    <span>{feature}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {extractedProducts.length === 0 && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6 flex items-start gap-3">
                <AlertCircle className="w-6 h-6 text-yellow-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-yellow-900 mb-1">No Products Found</h3>
                  <p className="text-yellow-700 text-sm">
                    The AI couldn&apos;t extract any products from this PDF. Try uploading a different file or use manual import.
                  </p>
                </div>
              </div>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
};
